import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation} from 'react-query';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {LoadingButton} from '@mui/lab';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {Delete, DeleteOutline} from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';

import AddFormDialog from './components/AddFormDialog';
import {ColumnDef} from '@tanstack/react-table';
import {Form, FormPresentationType, LayoutType, QuestionType} from "crqlar-react-common/dist/models/forms/form";
import {FormService} from "crqlar-react-common/dist/services/forms/form.service";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {multiPageThemePresets, singlePageThemePresets} from "../DesignPanel";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../../common/serverTable";
import SettingsListPage from "../../../system/layout/SettingsListPage";


export default function FormsList() {
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const readOnlyUser = useReadOnly();

    const [openDialog, setOpenDialog] = useState(false);
    // When rows are selected the toolbar becomes a focused delete-confirmation bar; this is its second step.
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const generateId = () => Math.random().toString(36).substring(2, 15);

    const createFormMutation = useMutation({
        mutationFn: async (formData: Form) => FormService.createForm(formData),
        onSuccess: (response) => {
            enqueueSnackbar(t('Form created successfully'), {variant: 'success'});
            if (response?.id) navigate(`/system/forms/${response.id}`);
        },
        onError: (error) => {
            enqueueSnackbar((error as Error).message || t('Failed to create form'), {variant: 'error'});
        }
    });

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    const handleCreateForm = (name: string, type: FormPresentationType, template?: Form) => {
        let newForm: Form;
        if (template) {
            newForm = {...template, title: name, description: template.description || '', version: 1};
        } else {
            const baseTheme = type === FormPresentationType.MULTI_PAGE
                ? multiPageThemePresets['indigo']
                : singlePageThemePresets['indigo'];
            newForm = {
                id: generateId(),
                title: name,
                description: '',
                version: 1,
                presentationType: type,
                questions: [{
                    id: generateId(),
                    type: QuestionType.WELCOME,
                    title: t('Welcome to our form'),
                    description: t('Please answer the following questions to help us understand your needs.'),
                    layout: LayoutType.DEFAULT
                }],
                settings: {showProgressBar: type === FormPresentationType.MULTI_PAGE},
                theme: {...baseTheme}
            };
        }
        createFormMutation.mutate(newForm);
        handleCloseDialog();
    };

    const columns = useMemo<ColumnDef<Form>[]>(() => [
        {
            id: 'name',
            header: t('Form Name'),
            accessorFn: form => form.title,
            cell: ({row}) => <Typography>{row.original.title}</Typography>,
            size: 240,
            meta: {defaultActive: true, grow: true, isServerSortable: true, serverSortField: 'title'}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: form => form.presentationType,
            cell: ({row}) => {
                const isMultiPage = row.original.presentationType === FormPresentationType.MULTI_PAGE;
                return (
                    <Chip
                        label={isMultiPage ? t('Multi-page') : t('Single-page')}
                        size="small"
                        color={isMultiPage ? 'primary' : 'secondary'}
                        sx={{textTransform: 'capitalize'}}
                    />
                );
            },
            size: 150,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'presentationType'}
        },
        {
            id: 'questionsCount',
            header: t('Questions'),
            accessorFn: form => form.questions?.length || 0,
            cell: ({row}) => <Typography variant="body2">{row.original.questions?.length || 0}</Typography>,
            size: 120,
            meta: {defaultActive: true}
        },
        {
            id: 'version',
            header: t('Version'),
            accessorFn: form => form.version,
            cell: ({row}) => <Typography>v{row.original.version}</Typography>,
            size: 100,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'version'}
        },
        {
            id: 'createdAt',
            header: t('Created'),
            accessorFn: form => form.insertDate,
            cell: ({row}) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarTodayIcon sx={{fontSize: 16}}/>
                    <Typography variant="body2">{formatDateDMYHM(row.original.insertDate)}</Typography>
                </Stack>
            ),
            size: 180,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'insertDate'}
        },
        {
            id: 'updatedAt',
            header: t('Last Updated'),
            accessorFn: form => form.updateDate,
            cell: ({row}) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <EditIcon sx={{fontSize: 16}}/>
                    <Typography variant="body2">{formatDateDMYHM(row.original.updateDate)}</Typography>
                </Stack>
            ),
            size: 180,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'updateDate'}
        },
        {
            id: 'submissions',
            header: t('Submissions'),
            cell: ({row}) => (
                <Tooltip title={t('View Submissions')}>
                    <Button
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/system/forms/${row.original.id}/submissions`);
                        }}
                        startIcon={<AssignmentIcon fontSize="small"/>}
                    >
                        {t('View')}
                    </Button>
                </Tooltip>
            ),
            size: 140,
            meta: {defaultActive: true}
        }
    ], [t, navigate]);

    const controller = useServerTable<Form>({
        tableId: 'system-forms',
        columns,
        getRowId: form => form.id ?? '',
        enableRowSelection: true,
        onRowClick: form => navigate(`/system/forms/${form.id}`),
        defaultPageSize: 10,
        queryFn: ({pagination, searchQuery, sorting}) =>
            FormService.listForms({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            }).then(page => ({content: page.content ?? [], totalElements: page.totalElements ?? 0}))
    });

    const selectedForms = controller.table.getSelectedRowModel().rows.map(row => row.original);
    const selectedCount = selectedForms.length;

    // Leaving selection (deselecting all) drops us out of the confirm step.
    useEffect(() => {
        if (selectedCount === 0) setConfirmingDelete(false);
    }, [selectedCount]);

    const deleteSelectedMutation = useMutation({
        mutationFn: async (ids: string[]) => FormService.deleteForm({id: ids}),
        onSuccess: () => {
            enqueueSnackbar(t('Forms deleted successfully'), {variant: 'success'});
            controller.table.resetRowSelection();
            setConfirmingDelete(false);
            controller.query.refetch();
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.message || t('Failed to delete forms'), {variant: 'error'});
            controller.query.refetch();
        }
    });

    return (
        <SettingsListPage title={t('Forms')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        {selectedCount > 0 ? (
                            // Selection mode: replace the whole toolbar with a single, focused delete flow.
                            <Box sx={{flexGrow: 1, width: '100%'}}>
                                {confirmingDelete ? (
                                    <Stack direction="row" alignItems="center" gap={1.5} sx={{width: '100%'}}>
                                        <DeleteOutline color="error"/>
                                        <Typography color="error.main" sx={{fontWeight: 600}}>
                                            {t('Delete {{count}} selected form(s)? This cannot be undone.', {count: selectedCount})}
                                        </Typography>
                                        <Box sx={{flexGrow: 1}}/>
                                        <Button onClick={() => setConfirmingDelete(false)}>
                                            {t('Cancel')}
                                        </Button>
                                        <LoadingButton
                                            color="error"
                                            variant="contained"
                                            startIcon={<Delete/>}
                                            loading={deleteSelectedMutation.isLoading}
                                            onClick={() => deleteSelectedMutation.mutate(selectedForms.map(form => form.id!))}
                                        >
                                            {t('Delete {{count}} form(s)', {count: selectedCount})}
                                        </LoadingButton>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" alignItems="center" gap={1.5} sx={{width: '100%'}}>
                                        <Typography sx={{fontWeight: 600}}>
                                            {t('{{count}} selected', {count: selectedCount})}
                                        </Typography>
                                        <Box sx={{flexGrow: 1}}/>
                                        <Button onClick={() => controller.table.resetRowSelection()}>
                                            {t('Clear selection')}
                                        </Button>
                                        <Button
                                            color="error"
                                            variant="contained"
                                            startIcon={<Delete/>}
                                            disabled={readOnlyUser}
                                            onClick={() => setConfirmingDelete(true)}
                                        >
                                            {t('Delete selected')}
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        ) : (
                            <>
                                <ServerTableSearch/>
                                <ServerTableSort/>
                                <ServerTableFullscreen/>
                                <ServerTableColumns/>
                                <ServerTableCreate
                                    label={t('Add Form')}
                                    disabled={readOnlyUser}
                                    onClick={handleOpenDialog}
                                />
                            </>
                        )}
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>

            <AddFormDialog
                open={openDialog}
                onClose={handleCloseDialog}
                onCreate={handleCreateForm}
                isCreating={createFormMutation.isLoading}
            />
        </SettingsListPage>
    );
}
